import {
  DEFAULT_QUOTE_TAX_RATE,
  type QuoteClientMatch,
  type QuoteDraftClient,
  type QuoteDraftLine,
} from './schemas/quote-drafts.js';

/**
 * Lot AT : logique pure du brouillon de devis (totaux, recherche du client, champs à compléter), commune au
 * serveur (plan `quote.create`, résumés) et à l'écran (totaux recalculés à chaque frappe).
 */

// ------------------------------------------------------------------ Totaux

export interface QuoteLineAmounts {
  /** Quantité × prix unitaire, avant remise (centimes HT). */
  grossCents: number;
  /** Montant de la remise (centimes HT). */
  discountCents: number;
  /** Total HT de la ligne, remise déduite. */
  netCents: number;
  /** TVA de la ligne (au taux de la ligne). */
  vatCents: number;
}

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/** Montants d'une ligne : arrondis au centime, remise bornée entre 0 et 100 %. */
export function quoteLineAmounts(
  line: Pick<QuoteDraftLine, 'quantity' | 'unitPriceCents' | 'discountPercent' | 'taxRate'>,
): QuoteLineAmounts {
  const quantity = clamp(line.quantity, 0, 100_000);
  const gross = Math.round(quantity * Math.max(0, Math.round(line.unitPriceCents)));
  const discount = Math.round((gross * clamp(line.discountPercent, 0, 100)) / 100);
  const net = gross - discount;
  const rate = Number.isFinite(line.taxRate) ? clamp(line.taxRate, 0, 100) : DEFAULT_QUOTE_TAX_RATE;
  return {
    grossCents: gross,
    discountCents: discount,
    netCents: net,
    vatCents: Math.round((net * rate) / 100),
  };
}

export interface QuoteTotals {
  grossCents: number;
  discountCents: number;
  /** Total HT (remises déduites). */
  netCents: number;
  vatCents: number;
  ttcCents: number;
  /** Taux de TVA présents (un seul : « TVA 20 % »). */
  taxRates: number[];
}

/** Totaux HT, remises, TVA et TTC d'un brouillon (TVA calculée ligne par ligne, à son taux). */
export function quoteTotals(
  lines: readonly Pick<QuoteDraftLine, 'quantity' | 'unitPriceCents' | 'discountPercent' | 'taxRate'>[],
): QuoteTotals {
  let gross = 0;
  let discount = 0;
  let net = 0;
  let vat = 0;
  const rates = new Set<number>();
  for (const line of lines) {
    const a = quoteLineAmounts(line);
    gross += a.grossCents;
    discount += a.discountCents;
    net += a.netCents;
    vat += a.vatCents;
    rates.add(Number.isFinite(line.taxRate) ? line.taxRate : DEFAULT_QUOTE_TAX_RATE);
  }
  return {
    grossCents: gross,
    discountCents: discount,
    netCents: net,
    vatCents: vat,
    ttcCents: net + vat,
    taxRates: [...rates].sort((a, b) => a - b),
  };
}

/** « TVA 20 % », « TVA 5,5 % et 20 % ». */
export function taxRatesLabel(rates: readonly number[]): string {
  const list = (rates.length > 0 ? rates : [DEFAULT_QUOTE_TAX_RATE]).map((r) => String(r).replace('.', ','));
  return `TVA ${list.length > 1 ? `${list.slice(0, -1).join(', ')} et ${list.at(-1)}` : list[0]} %`;
}

// ------------------------------------------------------------------ Recherche du client

/** Chiffres seulement (« 812 345 678 00021 » → « 81234567800021 »), null s'il n'y en a pas. */
export function siretDigits(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits === '' ? null : digits;
}

/** « 81234567800021 » → « 812 345 678 00021 » (un SIREN : « 812 345 678 »). */
export function formatSiret(value: string | null | undefined): string {
  const d = siretDigits(value);
  if (!d) return '';
  if (d.length === 14) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  if (d.length === 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  return d;
}

/** Formes juridiques et mots vides retirés pour comparer deux noms d'entreprise. */
const LEGAL_WORDS = new Set([
  'sarl',
  'sas',
  'sasu',
  'eurl',
  'sa',
  'sci',
  'snc',
  'ei',
  'eirl',
  'scop',
  'selarl',
  'earl',
  'gaec',
  'scea',
  'ste',
  'societe',
  'ets',
  'etablissements',
  'et',
  'de',
  'du',
  'des',
  'la',
  'le',
  'les',
  'l',
  'd',
]);

/** Mots significatifs d'un nom d'entreprise (sans accents, casse, ponctuation ni forme juridique). */
export function companyNameTokens(name: string | null | undefined): string[] {
  return (name ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w.length > 0 && !LEGAL_WORDS.has(w));
}

/** Nom normalisé (mots significatifs triés) : « Boulangerie Lemoine SARL » = « LEMOINE boulangerie ». */
export function normalizeCompanyName(name: string | null | undefined): string {
  return [...companyNameTokens(name)].sort().join(' ');
}

/** Ressemblance de deux noms : part des mots du plus court présents dans l'autre (0 à 1). */
export function companyNameSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const ta = new Set(companyNameTokens(a));
  const tb = new Set(companyNameTokens(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  const [small, big] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
  let common = 0;
  for (const w of small) if (big.has(w)) common += 1;
  return common / small.size;
}

/** Seuil de ressemblance au-delà duquel une fiche est signalée comme doublon possible. */
export const SIMILAR_NAME_THRESHOLD = 0.6;

export interface CompanyCandidate {
  axonautCompanyId: string;
  clientId: string | null;
  name: string;
  siret: string | null;
  city: string | null;
  createdOn: string | null;
}

/**
 * Fiches Axonaut qui correspondent au prospect, dans l'ordre de la décision de Maxime : d'abord par le SIRET
 * (même SIRET, ou même SIREN : un autre établissement de la même entreprise), puis par le nom (même nom une fois
 * les formes juridiques retirées), puis les noms qui ressemblent (doublons possibles). Une fiche n'apparaît qu'une
 * fois, sous la correspondance la plus forte.
 */
export function matchCompanies(
  prospect: { siret: string | null | undefined; name: string | null | undefined },
  companies: readonly CompanyCandidate[],
): QuoteClientMatch[] {
  const siret = siretDigits(prospect.siret);
  const siren = siret && siret.length >= 9 ? siret.slice(0, 9) : null;
  const name = normalizeCompanyName(prospect.name);
  const out: QuoteClientMatch[] = [];
  const seen = new Set<string>();
  const push = (c: CompanyCandidate, kind: QuoteClientMatch['kind']) => {
    if (seen.has(c.axonautCompanyId)) return;
    seen.add(c.axonautCompanyId);
    out.push({ ...c, kind });
  };
  if (siren) {
    for (const c of companies) {
      const other = siretDigits(c.siret);
      if (other && (other === siret || other.slice(0, 9) === siren)) push(c, 'siret');
    }
  }
  if (name) {
    for (const c of companies) if (normalizeCompanyName(c.name) === name) push(c, 'name');
    const similar = companies
      .map((c) => ({ c, score: companyNameSimilarity(prospect.name, c.name) }))
      .filter((s) => s.score >= SIMILAR_NAME_THRESHOLD)
      .sort((a, b) => b.score - a.score || a.c.name.localeCompare(b.c.name));
    for (const s of similar) push(s.c, 'similar');
  }
  return out.slice(0, 5);
}

/**
 * Fiche à retenir d'office : une correspondance par SIRET, sinon par nom (« Trouvé dans Axonaut »). Les noms qui
 * ressemblent seulement ne sont jamais pris d'office : on demande (« C'est le même ? »).
 */
export function autoMatch(matches: readonly QuoteClientMatch[]): QuoteClientMatch | null {
  return matches.find((m) => m.kind === 'siret') ?? matches.find((m) => m.kind === 'name') ?? null;
}

// ------------------------------------------------------------------ Champs à compléter

export interface MissingField {
  /** Chemin du champ dans le bloc client (`company.siret`, `contact.email`…). */
  field: string;
  label: string;
}

const blank = (value: string | null | undefined) => !value || value.trim() === '';

/**
 * Champs « à compléter » du bloc client (décision de Maxime : demandés, mis en avant, JAMAIS bloquants) : pour un
 * client à créer, raison sociale, SIRET, adresse ; pour le contact (à créer avec le client, ou à ajouter à une
 * fiche existante), nom, e-mail et téléphone.
 */
export function missingClientFields(client: QuoteDraftClient): MissingField[] {
  const out: MissingField[] = [];
  if (client.mode === 'new') {
    if (blank(client.company.name)) out.push({ field: 'company.name', label: 'Raison sociale' });
    if (blank(client.company.siret)) out.push({ field: 'company.siret', label: 'SIRET du siège' });
    if (blank(client.company.street) || blank(client.company.zipCode) || blank(client.company.city))
      out.push({ field: 'company.address', label: 'Adresse' });
  }
  const c = client.contact;
  const hasContact = !blank(c.firstname) || !blank(c.lastname) || !blank(c.email) || !blank(c.phone);
  if (client.mode === 'new' || hasContact) {
    if (blank(c.firstname) && blank(c.lastname)) out.push({ field: 'contact.name', label: 'Nom du contact' });
    if (blank(c.email)) out.push({ field: 'contact.email', label: 'E-mail du contact' });
    if (blank(c.phone)) out.push({ field: 'contact.phone', label: 'Téléphone du contact' });
  }
  return out;
}

/** « Claire Lemoine » → prénom « Claire », nom « Lemoine » (le premier mot est le prénom). */
export function splitPersonName(name: string | null | undefined): {
  firstname: string | null;
  lastname: string | null;
} {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { firstname: null, lastname: null };
  if (words.length === 1) return { firstname: words[0]!, lastname: null };
  return { firstname: words[0]!, lastname: words.slice(1).join(' ') };
}

/** Un numéro de mobile français (06, 07, +33 6, +33 7) : le champ « portable » d'Axonaut. */
export function isMobilePhone(phone: string | null | undefined): boolean {
  const digits = (phone ?? '').replace(/[^\d+]/g, '');
  return /^(0[67]\d{8}|\+33[67]\d{8}|0033[67]\d{8})$/.test(digits);
}

/** Bloc client vide (brouillon neuf). */
export function emptyQuoteClient(): QuoteDraftClient {
  return {
    mode: null,
    axonautCompanyId: null,
    clientId: null,
    foundBy: null,
    company: {
      name: null,
      siret: null,
      street: null,
      zipCode: null,
      city: null,
      country: null,
      isProspect: true,
    },
    contact: { firstname: null, lastname: null, role: null, email: null, phone: null },
    sources: {},
    dismissedMatches: [],
  };
}

/** Adresse sur une ligne (« 14 rue de la Marne, 44000 Nantes »). */
export function companyAddressLine(company: QuoteDraftClient['company']): string {
  const city = [company.zipCode, company.city].filter((p) => p && p.trim() !== '').join(' ');
  return [company.street, city].filter((p) => p && p.trim() !== '').join(', ');
}
